import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import api from "../../../axiosConfig";
import "./css/LongLifetimePage.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { popup } from "../../common/popup";

const emptyItem = {
  id: null,
  spesifikasi: "",
  part_name: "",
  lokasi: "",
  maker: "",
  stock: "",
  minimum_stock: "",
  keterangan: "",
  stock_old: 0,
};

export default function StockLongTab() {
  const [kategori, setKategori] = useState("bearing");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyItem);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState("");

  // Kategori (dikelola admin, read-only di sisi maintenance)
  const [categories, setCategories] = useState([]);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    fetchItems();
    setSearch("");
  }, [kategori]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get("/stock-items/categories");
        const list = res.data.data || [];
        setCategories(list);
        if (list.length && !list.includes(kategori)) setKategori(list[0]);
      } catch (err) {
        console.error("Gagal ambil kategori:", err);
      }
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get("/stock-items", {
        params: { lifetime_type: "long", category: kategori },
      });
      setItems(res.data || []);
    } catch (err) {
      console.error("FETCH ERROR:", err);
    }
  };

  /* ================= EXPORT EXCEL ================= */
  const exportToExcel = () => {
    if (items.length === 0) return popup.alert("Data masih kosong");

    const excelData = items.map((it, index) => ({
      No: index + 1,
      "Spesifikasi / Size": it.spesifikasi || "-",
      "Part Name": it.part_name,
      Lokasi: it.lokasi || "-",
      Maker: it.maker || "-",
      Stock: it.stock,
      "Minimum Stock": it.minimum_stock,
      Keterangan: it.keterangan || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 22 },
      { wch: 28 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
      { wch: 8 },
      { wch: 30 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Long Lifetime");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(fileData, `Stock_Long_Lifetime_${kategori.toUpperCase()}.xlsx`);
  };

  //Filter
  const filteredItems = items.filter((it) => {
    const keyword = search.toLowerCase();
    return (
      it.part_name?.toLowerCase().includes(keyword) ||
      it.spesifikasi?.toLowerCase().includes(keyword) ||
      it.lokasi?.toLowerCase().includes(keyword) ||
      it.maker?.toLowerCase().includes(keyword)
    );
  });

  /* ================= MODAL ================= */
  const openAddModal = () => {
    setIsEdit(false);
    setFormData(emptyItem);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setIsEdit(true);
    setFormData({
      ...item,
      stock_old: item.stock,
      stock: "",
      keterangan: "",
    });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  /* ================= SAVE (ADD / EDIT) ================= */
  const saveItem = async () => {
    try {
      setLoading(true);

      if (isEdit) {
        const qty = Number(formData.stock || 0);
        if (qty <= 0) {
          popup.alert("Jumlah stock baru harus lebih besar dari 0");
          return;
        }

        const payload = {
          qty: qty,
          note: formData.keterangan || "Tambah stock",
        };

        await api.post(`/stock-items/${formData.id}/add-stock`, payload);
      } else {
        if (!formData.part_name) {
          popup.alert("Part Name wajib diisi");
          return;
        }

        // ✅ CEK DUPLICATE DI SINI (AMAN)
        const isDuplicate = items.some(
          (it) =>
            it.spesifikasi?.toLowerCase() ===
              formData.spesifikasi?.toLowerCase() && it.category === kategori,
        );

        if (isDuplicate) {
          popup.error("Part Number sudah ada");
          return;
        }

        if (Number(formData.stock) <= 0) {
          popup.alert("Stock Awal harus lebih besar dari 0");
          return;
        }

        const payload = {
          lifetime_type: "long",
          category: kategori,
          spesifikasi: formData.spesifikasi || "",
          part_name: formData.part_name || "",
          lokasi: formData.lokasi || "",
          maker: formData.maker || "",
          stock: Number(formData.stock),
          minimum_stock: Number(formData.minimum_stock || 0),
          keterangan: formData.keterangan || "",
        };

        await api.post("/stock-items", payload);
      }

      popup.success(isEdit ? "Stock berhasil ditambahkan" : "Data berhasil disimpan");

      closeModal();
      fetchItems();
    } catch (err) {
      console.error("SAVE ERROR:", err.response?.data || err);
      popup.error("Gagal menyimpan data");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE ================= */
  const deleteItem = async (id) => {
    const ok = await popup.confirm("Yakin ingin menghapus item ini?");
    if (!ok) return;
    try {
      await api.delete(`/stock-items/${id}`);
      popup.success("Data berhasil dihapus");
      fetchItems();
    } catch (err) {
      console.error("DELETE ERROR:", err);
      popup.error("Gagal menghapus data");
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="longlife-container" style={{ marginTop: "-25px" }}>
      {/* HEADER */}
      <div className="header-bar">
        <h2>DATA STOCK LONG LIFETIME</h2>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            Export Excel
          </Button>
          <Button variant="primary" onClick={openAddModal}>
            Tambah Item
          </Button>
        </div>
      </div>

      {/* KATEGORI */}
      <div className="category-buttons">
        {categories.map((k) => (
          <button
            key={k}
            className={kategori === k ? "active" : ""}
            onClick={() => setKategori(k)}
          >
            {k.toUpperCase()}
          </button>
        ))}
        <Form.Control
          type="text"
          placeholder="🔍 Cari..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="danger bg-danger" onClick={() => setSearch("")}>
          Reset
        </Button>
      </div>

      {/* MODAL */}
      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? "Tambah Stock" : "Tambah Item Stock"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {["Spesifikasi", "Part Name", "Lokasi", "Maker"].map((field) => (
              <Form.Group className="mb-2" key={field}>
                <Form.Label>{field}</Form.Label>
                <Form.Control
                  value={formData[field.toLowerCase().replace(" ", "_")] || ""}
                  readOnly={isEdit}
                  onChange={(e) =>
                    handleChange(
                      field.toLowerCase().replace(" ", "_"),
                      e.target.value,
                    )
                  }
                />
              </Form.Group>
            ))}

            <div className="row">
              <div className="col">
                <Form.Label>
                  {isEdit ? "Stock Tambahan" : "Stock Awal"}
                </Form.Label>
                <Form.Control
                  type="number"
                  value={formData.stock || ""}
                  onChange={(e) => handleChange("stock", e.target.value)}
                />
              </div>

              {/* TAMBAHKAN MINIMUM STOCK HANYA SAAT TAMBAH ITEM */}
              {!isEdit && (
                <div className="col">
                  <Form.Label>Minimum Stock</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.minimum_stock || ""}
                    onChange={(e) =>
                      handleChange("minimum_stock", e.target.value)
                    }
                  />
                </div>
              )}
            </div>

            <Form.Group className="mt-2">
              <Form.Label>Keterangan</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.keterangan || ""}
                onChange={(e) => handleChange("keterangan", e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Batal
          </Button>
          <Button variant="primary" onClick={saveItem} disabled={loading}>
            {loading ? "Menyimpan..." : "Simpan"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TABLE */}
      <div className="table-wrapper mt-0">
        <table className="preview-table">
          <thead>
            <tr>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Part Number
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Part Name
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Lokasi</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Maker</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Stock Minimum
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Stock Actual
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty">
                  Data belum ada
                </td>
              </tr>
            ) : (
              filteredItems.map((it, i) => (
                <tr
                  key={it.id}
                  className={it.stock <= it.minimum_stock ? "low-stock" : ""}
                >
                  <td className="center">{i + 1}</td>
                  <td className="cell-left">{it.spesifikasi || "-"}</td>
                  <td className="bold cell-left">{it.part_name}</td>
                  <td className="center">{it.lokasi || "-"}</td>
                  <td className="center">{it.maker || "-"}</td>
                  <td className="center">{it.minimum_stock}</td>
                  <td className="center">{it.stock}</td>
                  <td className="center">
                    <Button
                      size="sm"
                      variant="warning"
                      className="me-1"
                      onClick={() => openEditModal(it)}
                    >
                      Tambah Stock
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => deleteItem(it.id)}
                    >
                      Hapus
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
